import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { twJoin } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import { match, P } from 'ts-pattern'
import FluentMoreVertical24Filled from '~icons/fluent/more-vertical-24-filled'
import MembershipBanner from 'src/components/Membership/MembershipBanner'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import QuotaLogList from 'src/components/Quota/QuotaLogList'
import { UIButton } from 'src/components/ui/Button'
import { UIMenu, UIMenuItem, UIMenuTrigger } from 'src/components/ui/Menu'
import Text from 'src/components/ui/Text'
import { UserProfileContext } from 'src/components/UserProfile/UserProfile.context'
import { showMembershipAutoRenewDialog } from 'src/features/Membership/components/MembershipAutoRenewalDialog'
import {
  getMembershipTierConfig,
  getSubscriptionState,
} from 'src/features/Membership/utils/subscriptionStateUtils'
import { useGetUserMembership } from 'src/services/apollo/membership'
import { useGetUserQuota } from 'src/services/apollo/quota'
import dayjs from 'src/utils/dayjs'
import { track } from 'src/utils/tracker'

export * from 'src/components/Quota/QuotaHelpIcon'

export default function UserCreditView(props: { userId: string }) {
  return (
    <div className="flex flex-col gap-12 relative">
      <MembershipSection />
      <CreditBalanceSection userId={props.userId} />
      <CreditLogSection userId={props.userId} />
    </div>
  )
}

const sectionStyles = tv({
  slots: {
    root: 'flex flex-col gap-4',
    heading: 'text-xl font-semibold',
  },
})
const dataListStyles = tv({
  slots: {
    list: 'grid grid-cols-[auto_1fr] content-start gap-x-8 gap-y-2',
    item: 'contents',
    title: 'text-neutral-600 dark:text-neutral-400',
    value: 'max-msm:justify-self-end',
  },
  variants: {
    type: {
      credit: {
        value: 'font-quicksand font-bold text-theme-primary',
      },
    },
  },
})

function CreditBalanceSection(props: { userId: string }) {
  const { userId } = props
  const { data } = useGetUserQuota(userId)
  const classNames = sectionStyles()
  const dataListClassNames = dataListStyles({ type: 'credit' })

  if (!data) return null

  return (
    <section className={classNames.root()}>
      <header className="flex items-end gap-4">
        <h4 className={classNames.heading()}>
          <Text i18nKey="user:credits" />
        </h4>
      </header>
      <div>
        <dl className={dataListClassNames.list()}>
          <div className={dataListClassNames.item()}>
            <dt className={dataListClassNames.title()}>
              <Text i18nKey="user:credits-breakdown.total" />
            </dt>
            <dd className={dataListClassNames.value()}>
              {data.total?.toLocaleString() ?? '0'}
            </dd>
          </div>
          <div className={dataListClassNames.item()}>
            <dt className={dataListClassNames.title()}>
              <Text i18nKey="user:credits-breakdown.paid" />
            </dt>
            <dd className={dataListClassNames.value()}>
              {data.paid?.toLocaleString() ?? '0'}
            </dd>
          </div>
          <div className={dataListClassNames.item()}>
            <dt className={dataListClassNames.title()}>
              <Text i18nKey="user:credits-breakdown.free" />
            </dt>
            <dd className={dataListClassNames.value()}>
              {data.free?.toLocaleString() ?? '0'}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  )
}

function MembershipSection() {
  const { t } = useTranslation(['membership'])
  const { user, isMyProfile } = useContext(UserProfileContext)
  const { data } = useGetUserMembership(user.id)
  const subscriptionState = useMemo(() => {
    return data?.subscription && getSubscriptionState(data.subscription)
  }, [data])
  // Whether the subscription is set to auto-renewal
  const autoRenewal = match(data)
    // For mobile subscriptions, inform user to check the status on app store
    .with(
      {
        subscription: {
          provider: P.union(
            P.string.startsWith('apple'),
            P.string.startsWith('google'),
          ),
        },
      },
      () => 'membership:membership.auto-renewal.messages.check-status-on-store',
    )
    // Otherwise, check whether the subscription is set to cancel at the end of the period
    .otherwise(() => !data?.subscription?.cancelAtPeriodEnd)

  const sectionClassNames = sectionStyles()
  const dataListClassNames = dataListStyles()

  return (
    <section className={sectionClassNames.root()}>
      <header className="flex items-end gap-4">
        <h4
          className={sectionClassNames.heading()}
          onClick={() => {
            if (import.meta.env.DEV) {
              console.log({ data })
            }
          }}
        >
          <Text i18nKey="membership:membership.label" />
        </h4>
        <UIMenuTrigger>
          <UIButton
            variant="ghost"
            icon={<FluentMoreVertical24Filled className="size-6" />}
            className="ml-auto"
          />
          <UIMenu placement="bottom end">
            <UIMenuItem
              color="normal"
              disabled={!isMyProfile}
              onAction={() => {
                track('plan_cancel_click', { scene: 'profile' })
                showMembershipAutoRenewDialog()
              }}
            >
              <Text i18nKey="membership:membership.auto-renewal.manage.menu-item" />
            </UIMenuItem>
          </UIMenu>
        </UIMenuTrigger>
      </header>
      <div>
        {data?.membership && (
          <dl className={dataListClassNames.list()}>
            {/* Current tier */}
            <div className={dataListClassNames.item()}>
              <dt className={dataListClassNames.title()}>
                <Text i18nKey="membership:membership.current-tier.label" />
              </dt>
              <dd
                className={dataListClassNames.value({
                  className: 'flex items-center gap-1',
                })}
              >
                <MembershipTierLabel tier={data?.membership?.tier} />
              </dd>
            </div>
            {/* Expire date */}
            {subscriptionState && subscriptionState.state === 'active' && (
              <div className={dataListClassNames.item()}>
                <dt className={dataListClassNames.title()}>
                  <Text i18nKey="membership:membership.expiry-date.label" />
                </dt>
                <dd
                  className={dataListClassNames.value({
                    className: 'flex flex-col max-msm:items-end',
                  })}
                >
                  <span>
                    {t('user:membership.expire-at', {
                      time: dayjs(subscriptionState.current.endAt).format('ll'),
                      days: dayjs(subscriptionState.current.endAt).diff(
                        dayjs(),
                        'day',
                      ),
                    })}
                  </span>
                  {subscriptionState.next && (
                    <p className="text-sm max-msm:text-right text-balance text-neutral-600 dark:text-neutral-400">
                      <Text
                        i18nKey="membership:membership.auto-renewal.messages.following-tier-change"
                        values={{
                          tier: 1,
                          time: t(
                            'membership:plan.action.subscribe.interval.month',
                          ),
                        }}
                        components={{
                          tier: <MembershipTierLabel />,
                          time: <span className="lowercase" />,
                        }}
                      />
                    </p>
                  )}
                </dd>
              </div>
            )}
            {/* Auto renewal */}
            <div className={dataListClassNames.item()}>
              <dt className={dataListClassNames.title()}>
                <Text i18nKey="membership:membership.auto-renewal.label" />
              </dt>
              <dd
                className={dataListClassNames.value({
                  className: 'inline-flex items-center gap-2',
                })}
              >
                {autoRenewal ? (
                  typeof autoRenewal === 'string' ? (
                    <p className="max-msm:text-right text-balance">
                      <Text i18nKey={autoRenewal} />
                    </p>
                  ) : (
                    <Text i18nKey="common:on" />
                  )
                ) : (
                  <Text i18nKey="common:off" />
                )}
              </dd>
            </div>
          </dl>
        )}
        <MembershipBanner
          scene="credits"
          className="mt-2 outline outline-1 -outline-offset-1 outline-black/10 rounded-2xl shadow-md"
        />
      </div>
    </section>
  )
}

function MembershipTierLabel(props: StyleProps & { tier?: string | number }) {
  if (!props.tier) return null

  return (
    <span
      className={twJoin('whitespace-nowrap', props.className)}
      style={props.style}
    >
      <MembershipIconCircled className="size-[1.2em] inline-grid align-text-top mr-0.5" />
      <span className="">
        <Text i18nKey={getMembershipTierConfig(Number(props.tier)).label} />
      </span>
    </span>
  )
}

function CreditLogSection(props: { userId: string }) {
  const classNames = sectionStyles()

  return (
    <section className={classNames.root()}>
      <header className="flex items-end gap-4">
        <h4 className={classNames.heading()}>
          <Text
            i18nKey="artwork:generation.credit-change-history"
            context="preview"
          />
        </h4>
      </header>
      <QuotaLogList userId={props.userId} />
    </section>
  )
}
